import { Issue, IssueState, IssueType } from "@boark/shared/types/issue";
import { IssueWithCreatedByAndLinksAndChildrenAndParentAndRegisters } from "@boark/shared/types/issue-with-created-by-and-links-and-children-and-parent-and-registers";

export type BoardColumn = "todo" | "inprogress" | "done-wontdo";

export type BoardCard = Pick<
  Issue,
  "identifier" | "title" | "state" | "type" | "updatedAt"
>;

export type BoardSwimlane = {
  id: string;
  title: string;
  groupIdentifier?: number;
  cards: BoardCard[];
};

export const ORPHANS_SWIMLANE_ID = "no-parent";

const FORTY_EIGHT_HOURS_MS = 48 * 60 * 60 * 1000;

export function issueStateToBoardColumn(state: IssueState): BoardColumn {
  switch (state) {
    case IssueState.TODO:
      return "todo";
    case IssueState.IN_PROGRESS:
      return "inprogress";
    case IssueState.DONE:
    case IssueState.WONTDO:
      return "done-wontdo";
  }
}

export function boardColumnToIssueState(
  column: BoardColumn,
  currentState: IssueState,
): IssueState {
  switch (column) {
    case "todo":
      return IssueState.TODO;
    case "inprogress":
      return IssueState.IN_PROGRESS;
    case "done-wontdo":
      if (currentState === IssueState.WONTDO) {
        return IssueState.WONTDO;
      }
      return IssueState.DONE;
  }
}

function sortCardsByUpdatedAt(cards: BoardCard[]): BoardCard[] {
  return [...cards].sort(
    (left, right) => right.updatedAt.getTime() - left.updatedAt.getTime(),
  );
}

function isUpdatedWithinLast48Hours(updatedAt: Date): boolean {
  return Date.now() - updatedAt.getTime() <= FORTY_EIGHT_HOURS_MS;
}

function toBoardCard(issue: Issue): BoardCard {
  return {
    identifier: issue.identifier,
    title: issue.title,
    state: issue.state,
    type: issue.type,
    updatedAt: issue.updatedAt,
  };
}

function includeOrphanIssue(issue: IssueWithCreatedByAndLinksAndChildrenAndParentAndRegisters): boolean {
  if (issue.type === IssueType.GROUP || issue.parent) {
    return false;
  }

  if (
    issue.state === IssueState.TODO ||
    issue.state === IssueState.IN_PROGRESS
  ) {
    return true;
  }

  return isUpdatedWithinLast48Hours(issue.updatedAt);
}

export function buildIssueBoardSwimlanes(
  issues: IssueWithCreatedByAndLinksAndChildrenAndParentAndRegisters[],
  orphansLaneTitle: string,
): BoardSwimlane[] {
  const openGroups = issues
    .filter(
      (issue) =>
        issue.type === IssueType.GROUP &&
        (issue.state === IssueState.TODO ||
          issue.state === IssueState.IN_PROGRESS),
    )
    .sort((left, right) => left.title.localeCompare(right.title));

  const groupSwimlanes: BoardSwimlane[] = openGroups.map((group) => ({
    id: String(group.identifier),
    title: group.title,
    groupIdentifier: group.identifier,
    cards: sortCardsByUpdatedAt(group.children.map(toBoardCard)),
  }));

  const orphanCards = sortCardsByUpdatedAt(
    issues.filter(includeOrphanIssue).map(toBoardCard),
  );

  return [
    ...groupSwimlanes,
    {
      id: ORPHANS_SWIMLANE_ID,
      title: orphansLaneTitle,
      cards: orphanCards,
    },
  ];
}

export function getParentIdentifierForSwimlane(
  swimlaneId: string,
  groupIdentifier?: number,
): number | null {
  if (swimlaneId === ORPHANS_SWIMLANE_ID) {
    return null;
  }
  if (groupIdentifier === undefined) {
    throw new Error("Group swimlane is missing group identifier");
  }
  return groupIdentifier;
}

export function canMoveIssueToSwimlane(
  issueType: IssueType,
  targetSwimlaneId: string,
): boolean {
  if (issueType === IssueType.GROUP) {
    return false;
  }
  if (targetSwimlaneId === ORPHANS_SWIMLANE_ID) {
    return true;
  }
  return issueType === IssueType.ISSUE;
}

export function groupCardsByColumn(
  cards: BoardCard[],
): Record<BoardColumn, BoardCard[]> {
  return {
    todo: sortCardsByUpdatedAt(
      cards.filter((card) => card.state === IssueState.TODO),
    ),
    inprogress: sortCardsByUpdatedAt(
      cards.filter((card) => card.state === IssueState.IN_PROGRESS),
    ),
    "done-wontdo": sortCardsByUpdatedAt(
      cards.filter(
        (card) =>
          card.state === IssueState.DONE || card.state === IssueState.WONTDO,
      ),
    ),
  };
}
